import { useState, useEffect, useMemo, useCallback } from 'react';
import type { ProjectStatus } from '../services/projectService';

// Versioned key — bump the suffix on any breaking change to ProjectFilterState's
// shape so a stale persisted blob is dropped rather than merged into a
// half-populated state. Mirrors useOrderFilters' persistence scheme.
const STORAGE_KEY = 'twm_projects_filter_state_v1';

/** Minimal row shape the filter operates on. The Projects page's ProjectRow
 *  satisfies this; keeping it structural lets applyFilters stay generic and
 *  return the caller's richer row type unchanged. */
export interface ProjectFilterRow {
  status: ProjectStatus;
  /** Creation time in ms epoch, or 0 when unknown. Drives the date range. */
  createdAtRaw: number;
}

export interface ProjectFilterState {
  statuses: ProjectStatus[];
  /** ISO date string (YYYY-MM-DD) or empty. Inclusive lower bound on the
   *  project's creation date. */
  dateFrom: string;
  /** ISO date string (YYYY-MM-DD) or empty. Inclusive upper bound on the
   *  project's creation date (pushed to end-of-day at apply time). */
  dateUntil: string;
  /** Filter sidebar open/closed. Owned here (not by useLayout) so the user's
   *  choice persists across navigation. */
  sidebarCollapsed: boolean;
}

const EMPTY_STATE: ProjectFilterState = {
  statuses: [],
  dateFrom: '',
  dateUntil: '',
  sidebarCollapsed: false,
};

function loadState(): ProjectFilterState {
  if (typeof window === 'undefined') return EMPTY_STATE;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const base = raw ? { ...EMPTY_STATE, ...JSON.parse(raw) } : EMPTY_STATE;
    return isMobile ? { ...base, sidebarCollapsed: true } : base;
  } catch {
    return isMobile ? { ...EMPTY_STATE, sidebarCollapsed: true } : EMPTY_STATE;
  }
}

function saveState(state: ProjectFilterState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / private mode — silently fail.
  }
}

export interface UseProjectFiltersResult {
  state: ProjectFilterState;
  setField: <K extends keyof ProjectFilterState>(key: K, value: ProjectFilterState[K]) => void;
  clearAll: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  applyFilters: <T extends ProjectFilterRow>(rows: T[]) => T[];
}

export function useProjectFilters(): UseProjectFiltersResult {
  const [state, setState] = useState<ProjectFilterState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const setField = useCallback(
    <K extends keyof ProjectFilterState>(key: K, value: ProjectFilterState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearAll = useCallback(() => {
    setState((prev) => ({ ...EMPTY_STATE, sidebarCollapsed: prev.sidebarCollapsed }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (state.statuses.length > 0) n++;
    if (state.dateFrom || state.dateUntil) n++;
    return n;
  }, [state]);

  const hasActiveFilters = activeFilterCount > 0;

  const applyFilters = useCallback<UseProjectFiltersResult['applyFilters']>(
    (rows) => {
      const fromTs = state.dateFrom ? new Date(state.dateFrom).getTime() : null;
      // Until is inclusive — push to end-of-day so a YYYY-MM-DD upper bound
      // includes projects created anywhere on that calendar day.
      const untilTs = state.dateUntil
        ? new Date(`${state.dateUntil}T23:59:59`).getTime()
        : null;

      return rows.filter((r) => {
        if (state.statuses.length > 0) {
          if (!state.statuses.includes(r.status)) return false;
        } else if (r.status === 'canceled') {
          // Default view hides canceled projects; staff opt in by checking
          // Canceled in the sidebar. (Mirrors Orders hiding terminal states.)
          return false;
        }

        if (fromTs !== null && r.createdAtRaw && r.createdAtRaw < fromTs) return false;
        if (untilTs !== null && r.createdAtRaw && r.createdAtRaw > untilTs) return false;

        return true;
      });
    },
    [state],
  );

  return { state, setField, clearAll, hasActiveFilters, activeFilterCount, applyFilters };
}
